import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { X, User, BookOpen, MessageSquare, ClipboardCheck, Calendar } from "lucide-react";
import { useNavigate } from "react-router-dom";

import "../../RessourcesStyles/TraitementDemandes.css";
import DemandeTrackingStepper from "./DemandeTrackingStepper";
import {
    clearDemandeTracking,
    getDemandeTrackingRequest,
} from "../../../../../redux/actions/demandesActions";

const fmt = (iso) => {
    if (!iso) return "-";
    try {
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return String(iso);
        return d.toLocaleString("fr-FR", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return String(iso);
    }
};

const safe = (v) => (v != null && String(v).trim() !== "" ? String(v) : null);

const getStatusLabel = (demande) => {
    const status = String(
        demande?.status ?? demande?.managerStatus ?? demande?.rfStatus ?? ""
    ).trim().toUpperCase();

    if (["APPROVED", "VALIDATED", "ACCEPTED", "RF_APPROVED", "MANAGER_APPROVED"].includes(status)) {
        return "Validee";
    }
    if (["REJECTED", "REFUSED", "RF_REJECTED", "MANAGER_REJECTED"].includes(status)) {
        return "Rejetee";
    }
    if (["CANCELLED", "WITHDRAWN"].includes(status)) {
        return "Annulee";
    }
    if (["IN_PROGRESS", "ONGOING", "PENDING_RF"].includes(status)) {
        return "En cours";
    }
    if (["PENDING", "PENDING_MANAGER", "PENDING_MANAGER_STEP"].includes(status)) {
        return "En attente";
    }
    return demande?.status || "En attente";
};

const getStatusBadgeClass = (demande) => {
    const label = getStatusLabel(demande);
    if (label === "Validee") return "traitement-status-badge--success";
    if (label === "Rejetee") return "traitement-status-badge--danger";
    if (label === "Annulee") return "traitement-status-badge--muted";
    if (label === "En cours") return "traitement-status-badge--info";
    if (label === "En attente") return "traitement-status-badge--warning";
    return "traitement-status-badge--muted";
};

const getDemandeTypeLabel = (demande) => {
    const raw = String(
        demande?.demandeType || demande?.type || demande?.source || demande?.nature || ""
    ).toUpperCase();

    if (["CATALOGUE", "FORMATION"].includes(raw)) return "Catalogue";
    if (["HORS_CATALOGUE", "HORS_CATALOG", "OUT_OF_CATALOG"].includes(raw) || raw.includes("HORS")) {
        return "Hors catalogue";
    }
    if (["EVENT", "EVENT_SESSION", "EVENEMENT"].includes(raw)) return "Evenement";
    if (raw === "SESSION") return "Session";
    return raw || "Autre";
};

const getStepStatusLabel = (value) => {
    const s = String(value || "").trim().toUpperCase();
    if (["APPROVED", "VALIDATED", "ACCEPTED"].includes(s)) return "Valide";
    if (["REJECTED", "REFUSED"].includes(s)) return "Rejete";
    if (["PENDING", "PENDING_MANAGER", "PENDING_RF"].includes(s)) return "En attente";
    return value || "-";
};

const DetailField = ({ label, value }) => (
    <div className="traitement-detail-field">
        <div className="traitement-detail-label">{label}</div>
        <div className="traitement-detail-value">
            {safe(value) ?? <span className="traitement-detail-null">-</span>}
        </div>
    </div>
);

const DetailSection = ({ title, icon, children }) => (
    <div className="traitement-detail-section">
        <div className="traitement-detail-section-title">
            <span className="traitement-detail-section-icon">{icon}</span>
            {title}
        </div>
        <div className="traitement-detail-grid">{children}</div>
    </div>
);

export default function DemandeDetailsModal({ open, demande, onClose }) {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { selectedDemandeTracking, loadingTracking } = useSelector((state) => state.demandes || {});

    useEffect(() => {
        if (!open || !demande?.id) return;
        dispatch(getDemandeTrackingRequest(demande.id));
    }, [dispatch, demande?.id, open]);

    useEffect(() => {
        if (open) return undefined;
        dispatch(clearDemandeTracking());
        return undefined;
    }, [dispatch, open]);

    if (!open || !demande) return null;

    const d = demande;
    const statusLabel = getStatusLabel(d);
    const statusClass = getStatusBadgeClass(d);
    const typeLabel = getDemandeTypeLabel(d);

    return (
        <div className="tdModal" onMouseDown={(e) => e.target === e.currentTarget && onClose?.()}>
            <div className="tdModal__panel">
                <div className="tdModal__header">
                    <div className="tdModal__headLeft">
                        <div className="tdModal__icon">
                            <BookOpen size={18} />
                        </div>
                        <div>
                            <div className="tdModal__title">Détails de la demande</div>
                            <div className="tdModal__sub">
                                Ref. {d?.reference || d?.numero || `DF-${d?.id}`}
                            </div>
                        </div>
                    </div>
                    <button type="button" className="tdIconBtn" onClick={onClose}>
                        <X size={16} />
                    </button>
                </div>

                <div className="tdModal__body">
                    <div className="traitement-detail-card">
                        <div className="traitement-detail-hero">
                            <div className="traitement-detail-hero-left">
                                <div className="traitement-detail-hero-avatar">
                                    {(d?.targetUserName || "?")[0].toUpperCase()}
                                </div>
                                <div>
                                    <div className="traitement-detail-hero-name">
                                        {safe(d?.formationNom) || safe(d?.formationName) || safe(d?.eventTitre) || safe(d?.action) || "Demande de formation"}
                                    </div>
                                    <div className="traitement-detail-hero-sub">
                                        {safe(d?.targetUserName) || "-"}
                                        {d?.campagneNom ? <span className="traitement-detail-hero-campagne"> . {d.campagneNom}</span> : null}
                                    </div>
                                </div>
                            </div>
                            <div className="traitement-detail-hero-right">
                                <span className={`traitement-status-badge ${statusClass}`}>{statusLabel}</span>
                                <span className="traitement-type-badge">{typeLabel}</span>
                            </div>
                        </div>

                        <DetailSection title="Collaborateur" icon={<User size={14} />}>
                            <DetailField label="Nom complet" value={d?.targetUserName} />
                            <DetailField label="Matricule" value={d?.matricule || d?.targetUserMatricule} />
                            <DetailField label="Entite / Departement" value={d?.entite || d?.department || d?.departement} />
                            <DetailField label="Manager" value={d?.managerName} />
                            <DetailField label="Demandeur" value={d?.createdByName} />
                            <DetailField label="Campagne" value={d?.campagneNom} />
                        </DetailSection>

                        <DetailSection title="Formation / Demande" icon={<BookOpen size={14} />}>
                            <DetailField label="Intitule formation" value={d?.formationNom || d?.formationName || d?.action} />
                            <DetailField label="Domaine" value={d?.domaineNom || d?.domain} />
                            <DetailField label="Theme" value={d?.themeNom || d?.theme} />
                            <DetailField label="Type" value={typeLabel} />
                            <DetailField label="Mode" value={d?.mode} />
                            <DetailField label="Niveau" value={d?.niveau} />
                            <DetailField label="Priorite" value={d?.priorite != null ? `P${d.priorite}` : null} />
                            <DetailField label="Certifiante" value={d?.certifiante != null ? (d.certifiante ? "Oui" : "Non") : null} />
                            <DetailField label="Periode" value={d?.period} />
                            <DetailField label="Organisme" value={d?.organisme || d?.fournisseur || d?.provider} />
                            <DetailField label="Date de début" value={fmt(d?.dateDebut || d?.startDate)} />
                            <DetailField label="Date de fin" value={fmt(d?.dateFin || d?.endDate)} />
                            <DetailField label="Duree" value={d?.duree || d?.duration} />
                        </DetailSection>

                        <DetailSection title="Justification & Commentaires" icon={<MessageSquare size={14} />}>
                            <DetailField label="Commentaire / description" value={d?.description || d?.action} />
                            <DetailField label="Objectif 1" value={d?.objectifs} />
                            <DetailField label="Objectif 2" value={d?.objectifs2} />
                            <DetailField label="Objectif 3" value={d?.objectifs3} />
                            <DetailField label="Commentaire manager" value={d?.managerComment} />
                            <DetailField label="Commentaire RF" value={d?.rfComment} />
                        </DetailSection>

                        <DetailSection title="Decisions & Traitement" icon={<ClipboardCheck size={14} />}>
                            <DetailField label="Statut global" value={statusLabel} />
                            <DetailField label="Statut manager" value={getStepStatusLabel(d?.managerStepStatus)} />
                            <DetailField label="Statut RF" value={getStepStatusLabel(d?.rfStepStatus)} />
                            <DetailField label="Decision manager par" value={d?.managerDecisionByName} />
                            <DetailField label="Decision RF par" value={d?.rfDecisionByName} />
                            <DetailField label="Date decision manager" value={fmt(d?.managerDecisionAt)} />
                            <DetailField label="Date decision RF" value={fmt(d?.rfDecisionAt)} />
                            <DetailField label="Date de demande" value={fmt(d?.createdAt)} />
                            <DetailField label="Derniere mise a jour" value={fmt(d?.updatedAt)} />
                        </DetailSection>

                        {(safe(d?.cancelReason) ||
                            safe(d?.cancelComment) ||
                            safe(d?.withdrawReason) ||
                            safe(d?.withdrawComment) ||
                            safe(d?.cancelRequestedAt) ||
                            safe(d?.withdrawRequestedAt)) && (
                            <DetailSection title="Historique complementaire" icon={<Calendar size={14} />}>
                                <DetailField label="Motif d'annulation" value={d?.cancelReason} />
                                <DetailField label="Commentaire annulation" value={d?.cancelComment} />
                                <DetailField label="Date d'annulation" value={fmt(d?.cancelRequestedAt)} />
                                <DetailField label="Motif de desistement" value={d?.withdrawReason} />
                                <DetailField label="Commentaire desistement" value={d?.withdrawComment} />
                                <DetailField label="Date de desistement" value={fmt(d?.withdrawRequestedAt)} />
                            </DetailSection>
                        )}

                        <div className="traitement-detail-section">
                            <div className="traitement-detail-section-title">
                                <span className="traitement-detail-section-icon">
                                    <Calendar size={14} />
                                </span>
                                Suivi de la demande
                            </div>
                            <div style={{ marginTop: 12 }}>
                                {loadingTracking ? (
                                    <div className="traitement-detail-null">Chargement du suivi...</div>
                                ) : selectedDemandeTracking ? (
                                    <DemandeTrackingStepper
                                        tracking={selectedDemandeTracking}
                                        onNavigate={(targetUrl) => {
                                            if (targetUrl) navigate(targetUrl);
                                        }}
                                    />
                                ) : (
                                    <div className="traitement-detail-null">Aucun suivi disponible.</div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="tdModal__footer">
                    <button type="button" className="tdBtn tdBtn--ghost" onClick={onClose}>
                        Fermer
                    </button>
                </div>
            </div>
        </div>
    );
}
